"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@/shared/ui/buttons/button";
import { Skeleton } from "@/shared/ui/data-display/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/ui/data-display/avatar";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/shared/ui/modals/dialog";
import { BOARD_DIALOG_CLASS, BOARD_DIALOG_HEADER, DialogBody } from "./dialog-body";
import { ToggleGroup, ToggleGroupItem } from "@/shared/ui/buttons/toggle-group";
import { WriteAboutPostButton, WriteDialog, type WriteTarget } from "./write-dialog";
import {
  useLfgLftApplicationsIncoming,
  useLfgLftApplicationsOutgoing,
  usePatchLfgLftApplication,
} from "@/hooks/useLfgLft";
import type { LfgLftApplicationResponse } from "@/shared/api/lfg-lft-types";
import { cn } from "@/shared/lib/utils";
import { isLfgRole, toLfgRank } from "../model/board-filters";
import {
  APPLICATION_TABS,
  actionsFor,
  parseApplicationStatus,
  sortForInbox,
  splitIncoming,
  type ApplicationAction,
  type ApplicationTab,
} from "../model/applications";
import { LfgRoleIcon, RankEmblem } from "./board-card-parts";

const SEGMENT_ON = "data-[state=on]:bg-highlight-soft data-[state=on]:text-highlight";
const PRIMARY_BTN =
  "h-[34px] border-highlight-border bg-highlight-soft px-3.5 font-bold text-highlight hover:bg-highlight-soft/80 hover:text-highlight";

const STATUS_CHIP: Record<string, string> = {
  Pending: "bg-muted text-muted-foreground",
  Accepted: "bg-highlight-soft text-highlight",
  Declined: "bg-muted text-muted-foreground",
  Withdrawn: "bg-muted text-muted-foreground",
};

interface ApplicationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ApplicationsDialog({ open, onOpenChange }: ApplicationsDialogProps) {
  const t = useTranslations("LfgBoard");
  const [tab, setTab] = useState<ApplicationTab>("teams");
  const incoming = useLfgLftApplicationsIncoming(open);
  const outgoing = useLfgLftApplicationsOutgoing(open);
  const patch = usePatchLfgLftApplication();
  // «Написать» открывает отклик вместо диалога заявок (макет), а не поверх него.
  const [writeTarget, setWriteTarget] = useState<WriteTarget | null>(null);

  const { teams, players } = splitIncoming(incoming.data ?? []);
  const query = tab === "mine" ? outgoing : incoming;
  const items = sortForInbox(tab === "teams" ? teams : tab === "players" ? players : (outgoing.data ?? []));

  const run = (id: string, action: ApplicationAction) => {
    const status = { accept: "Accepted", decline: "Declined", restore: "Pending", cancel: "Pending", withdraw: "Withdrawn" } as const;
    if (action !== "message") patch.mutate({ id, body: { status: status[action] } });
  };

  return (
    <>
    <Dialog open={open && !writeTarget} onOpenChange={onOpenChange}>
      <DialogContent className={BOARD_DIALOG_CLASS}>
        <DialogHeader className={BOARD_DIALOG_HEADER}>
          <DialogTitle className="font-heading text-[22px]">{t("apps.title")}</DialogTitle>
          <DialogDescription className="sr-only">{t("apps.matchHint")}</DialogDescription>
        </DialogHeader>

        <DialogBody>
        <ToggleGroup
          type="single"
          value={tab}
          onValueChange={(v) => {
            const next = APPLICATION_TABS.find((x) => x === v);
            if (next) setTab(next);
          }}
          aria-label={t("apps.title")}
          className="w-full gap-1.5 rounded-xl border border-border bg-muted/30 p-1.5"
        >
          {APPLICATION_TABS.map((x) => (
            <ToggleGroupItem key={x} value={x} className={cn("h-[34px] flex-1 rounded-lg! text-[13px] font-bold text-muted-foreground", SEGMENT_ON)}>
              {t(x === "teams" ? "apps.tabTeams" : x === "players" ? "apps.tabPlayers" : "apps.tabMine")}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>

        {query.isLoading ? (
          <div className="space-y-2.5">{[1, 2].map((i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}</div>
        ) : query.isError ? (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border p-8 text-center">
            <p className="text-sm text-muted-foreground">{t("apps.error")}</p>
            <Button variant="outline" size="sm" onClick={() => void query.refetch()}>{t("apps.retry")}</Button>
          </div>
        ) : items.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            {t(tab === "teams" ? "apps.emptyTeams" : tab === "players" ? "apps.emptyPlayers" : "apps.emptyMine")}
          </p>
        ) : (
          <ul className="flex flex-col gap-2.5">
            {items.map((a) => (
              <li key={a.id}>
                <ApplicationCard app={a} side={tab === "mine" ? "outgoing" : "incoming"} busy={patch.isPending} onAction={(action) => run(a.id, action)} onWrite={setWriteTarget} />
              </li>
            ))}
          </ul>
        )}
        </DialogBody>
      </DialogContent>
    </Dialog>
    <WriteDialog
      target={writeTarget}
      onClose={() => {
        setWriteTarget(null);
        onOpenChange(false);
      }}
    />
    </>
  );
}

function ApplicationCard({
  app,
  side,
  busy,
  onAction,
  onWrite,
}: {
  onWrite: (target: WriteTarget) => void;
  app: LfgLftApplicationResponse;
  side: "incoming" | "outgoing";
  busy: boolean;
  onAction: (a: ApplicationAction) => void;
}) {
  const t = useTranslations("LfgBoard");
  const status = parseApplicationStatus(app.status);
  const invite = app.kind === "InviteLfgPlayer";

  // Кто на карточке: входящее приглашение — позвавшая команда, заявка — сам игрок,
  // исходящее — адресат (у команды капитан).
  const person = side === "outgoing" ? app.addressee : invite ? null : app.actor;
  const name = side === "outgoing" ? (person?.displayName ?? "—") : invite ? (app.sourceTeam?.displayName ?? app.actor.displayName) : app.actor.displayName;
  const avatar = side === "outgoing" ? person?.avatarUrl : invite ? (app.sourceTeam?.avatarUrl ?? app.actor.avatarUrl) : app.actor.avatarUrl;
  const meta =
    side === "outgoing" ? t(invite ? "apps.kindMineInvite" : "apps.kindMineApply") : t(invite ? "apps.kindInvite" : "apps.kindApply");
  const role = isLfgRole(app.targetRole) ? app.targetRole : null;
  // Написать — человеку, а не команде: у приглашения это позвавший капитан.
  const writeTo = side === "outgoing" ? app.addressee : app.actor;
  const rank = !invite && side === "incoming" && app.actor.rank ? toLfgRank(app.actor.rank) : null;

  return (
    <article className="rounded-xl border border-border bg-muted/20 p-3">
      <div className="flex items-center gap-2.5">
        <Avatar className="size-9 flex-none">
          <AvatarImage src={avatar ?? undefined} alt="" />
          <AvatarFallback className="font-logo text-[13px]">{name.slice(0, 2).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-bold">{name}</h3>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
            {rank && <RankEmblem rank={rank} size={20} />}
            {role && (
              <span className="inline-flex items-center gap-1" title={t(`role.${role}`)}>
                <LfgRoleIcon role={role} size={16} />
                {t(`role.${role}`)}
              </span>
            )}
            <span>{meta}</span>
          </div>
        </div>
        <span className={cn("inline-flex h-[22px] flex-none items-center rounded-md px-2 text-[11px] font-bold", STATUS_CHIP[status])}>
          {t(`apps.status.${status}`)}
        </span>
      </div>

      {app.message && <p className="mt-2.5 whitespace-pre-wrap text-[13px] font-semibold leading-normal">{app.message}</p>}
      {status === "Accepted" && <p className="mt-2 text-xs text-muted-foreground">{t("apps.matchHint")}</p>}

      <div className="mt-2.5 flex flex-wrap gap-2">
        {actionsFor(status, side, app.post.status !== "Closed").map((action) =>
          action === "message" ? (
            <WriteAboutPostButton
              key={action}
              onOpen={onWrite}
              label={t("apps.message")}
              className={PRIMARY_BTN}
              target={{
                postId: app.post.id,
                toUserId: writeTo?.userId,
                toName: name,
                recipientName: writeTo?.displayName,
                applicationId: app.id,
                kindLabel: meta,
                card: { header: { name, avatarUrl: avatar, team: invite && side === "incoming" }, description: app.message ?? undefined, meta: t("write.accepted") },
              }}
            />
          ) : (
            <Button
              key={action}
              variant="outline"
              disabled={busy}
              className={cn("h-[34px]", action === "accept" ? PRIMARY_BTN : "text-muted-foreground")}
              onClick={() => onAction(action)}
            >
              {t(`apps.${action}`)}
            </Button>
          ),
        )}
      </div>
    </article>
  );
}
